// 데모 번들 로더 + 서버 규칙 재현(표 페이지/정렬/검색·섹션 필터).
//
// 번들 구조·키 규약은 sales-server/script/demo_report/README.md 와 계약이다:
//   report/{item}/{scope}.json → variants[key]
//   key = item|scope|basis|origin|mode|grain|dimension|token|couponId  ('-' = 미지정)
import { DEMO_BASE, isDemoActive } from "./config";

const cache = new Map();

export const loadJson = (rel) => {
  if (!cache.has(rel)) {
    const p = fetch(`${DEMO_BASE}/${rel}`).then((r) => {
      if (!r.ok) throw new Error(`demo bundle missing: ${rel} (${r.status})`);
      return r.json();
    });
    p.catch(() => cache.delete(rel));
    cache.set(rel, p);
  }
  return cache.get(rel);
};

export const loadManifest = () => loadJson("manifest.json");

let manifestSync = null;
// 데모가 켜진 세션에서만 선로드 — 일반 사용자에게는 요청 0
if (isDemoActive()) {
  loadJsonSafe("manifest.json").then((m) => {
    manifestSync = m;
  });
}
/** 이미 도착한 manifest (동기) — 없으면 null */
export const getManifestSync = () => manifestSync;

function loadJsonSafe(rel) {
  try {
    return loadJson(rel).catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
}

// 이벤트 목록은 상세 화면 초기 날짜(이벤트 기간)에 동기적으로 필요하다
let eventsSync = null;
export const loadEvents = () =>
  loadJson("event/list.json").then((d) => {
    eventsSync = d;
    return d;
  });
export const getCachedEvent = (eventId) =>
  (eventsSync?.items || []).find((e) => e.id === eventId) || null;

export const variantKey = (
  item,
  scope,
  { basis, origin, mode, grain, dimension, token, couponId } = {},
) =>
  [item, scope || "hq"]
    .concat(
      [basis, origin, mode, grain, dimension, token, couponId].map((v) =>
        v === undefined || v === null || v === "" ? "-" : String(v),
      ),
    )
    .join("|");

// ---- 서버 규칙 재현 (report_v2.py §11) ----
const TABLE_SORT_KEYS = {
  salesSummary: ["date"],
  storeList: ["paidAmount", "returnRate", "avgOrderValue"],
  product: ["paidAmount", "returnedQuantity"],
};

const sortValue = (row, key) => {
  let v = row?.[key];
  if (v && typeof v === "object" && "value" in v) v = v.value;
  return v === null || v === undefined ? 0 : v;
};

/** `_apply_table_page` 동형 — rows 정렬+슬라이스, meta.page 반환 */
export const applyTablePage = (data, item, tableKey, page, size, sort) => {
  const table = data?.[tableKey];
  if (!table || !Array.isArray(table.rows)) return null;
  let rows = table.rows.slice();
  if (sort) {
    const [keyRaw, dirRaw] = String(sort).split(":");
    const key = (keyRaw || "").trim();
    const desc = (dirRaw || "desc").trim().toLowerCase() !== "asc";
    if ((TABLE_SORT_KEYS[item] || []).includes(key)) {
      // 안정 정렬 (Array.prototype.sort 는 ES2019+ 안정)
      rows.sort((a, b) => {
        const av = sortValue(a, key);
        const bv = sortValue(b, key);
        if (av === bv) return 0;
        const c = av > bv ? 1 : -1;
        return desc ? -c : c;
      });
    }
  }
  const p = Math.max(1, Number(page) || 1);
  const s = Math.max(1, Number(size) || 10);
  const total = rows.length;
  table.rows = rows.slice((p - 1) * s, (p - 1) * s + s);
  return { page: p, size: s, total };
};

/** `_filter_rows_by_name` 동형 — 매장명/제품명 부분일치(대소문자 무시) */
export const filterRowsByName = (rows, q, keys = ["locationName", "productName", "name"]) => {
  if (!q || !String(q).trim()) return rows;
  const ql = String(q).trim().toLowerCase();
  return (rows || []).filter((r) =>
    keys.some((k) => String(r?.[k] || "").toLowerCase().includes(ql)),
  );
};

/** options.sections include-list — 에코 스칼라(mode/grain/dimension 등)는 항상 유지 */
export const pickSections = (data, sections) => {
  if (!data || !Array.isArray(sections) || !sections.length) return data;
  const out = {};
  Object.keys(data).forEach((k) => {
    const v = data[k];
    const scalar = v === null || ["string", "number", "boolean"].includes(typeof v);
    if (scalar || sections.includes(k)) out[k] = v;
  });
  return out;
};

export const deepClone = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)));

/** 봉투가 없을 때 — 계약 §2 모양의 빈 봉투 + NOT_AVAILABLE */
export const emptyEnvelope = (item, message = "데모 번들에 없는 조회입니다") => ({
  meta: { item, scope: "hq", basis: "pay", period: null, compare: null, source: { fact: true } },
  data: null,
  warnings: [{ code: "NOT_AVAILABLE", message, detail: { item } }],
});
